import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  User as UserIcon, Building2, Bell, Lock, Plug, LogOut, Mail,
  CheckCircle2, ChevronRight, AlertCircle, Sparkles, BellOff, Volume2,
  FlaskConical, Sprout, Loader2, RefreshCw, Workflow, Network, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { useAuth } from "../data/authStore";
import { useChainStore } from "../data/chainStore";
import { api } from "../lib/api";
import { RoleGate } from "../hooks/useRoleGate";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import AutomationsPage from "./AutomationsPage";
import IntegrationInfrastructurePage from "./IntegrationInfrastructurePage";
import OrganisationInfrastructurePage from "./OrganisationInfrastructurePage";
import { useOperationalVisibility } from "../hooks/useOperationalVisibility";
import { AUDIENCE_INTERNAL, AUDIENCE_CLIENT } from "../constants/operationalMomentum";

import { PremiumInput } from "../components/brand/PremiumInput";
import {
  getMutedDeals, unmuteDeal, requestNotificationPermission, canShowNative,
} from "../lib/notify";
import { evaluateSeedOutcome } from "../lib/seedErrors";
import { SEED_EVALUATOR_VERSION, BUNDLE_BUILT_AT } from "../lib/buildVersion";

const PANEL_STYLE = {
  background: "linear-gradient(180deg, rgba(20,22,46,0.78) 0%, rgba(15,17,38,0.78) 100%)",
  border: "1px solid rgba(99,102,241,0.10)",
  backdropFilter: "blur(14px) saturate(140%)",
  WebkitBackdropFilter: "blur(14px) saturate(140%)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset, 0 14px 30px -22px rgba(0,0,0,0.50)",
};

const initialsOf = (s) => {
  if (!s) return "?";
  return String(s).trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

const SECTIONS = [
  { id: "profile",       label: "Profile",       Icon: UserIcon },
  { id: "workspace",     label: "Workspace",     Icon: Building2 },
  { id: "automations",   label: "Automations",   Icon: Workflow },
  { id: "integrations",  label: "Integrations",  Icon: Plug },
  { id: "organisation",  label: "Organisation",  Icon: Network },
  { id: "notifications", label: "Notifications", Icon: Bell },
  { id: "visibility",    label: "Operational visibility", Icon: Eye },
  { id: "security",      label: "Security",      Icon: Lock },
];

const Card = ({ title, subtitle, children, testid }) => (
  <section className="rounded-2xl p-6" style={PANEL_STYLE} data-testid={testid}>
    <div className="mb-5">
      <h3 className="font-display text-[16px] font-bold text-white tracking-tight">{title}</h3>
      {subtitle && <p className="text-[11.5px] text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Field = ({ label, children, hint }) => (
  <div className="mb-4 last:mb-0">
    <label className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[10.5px] text-slate-500 mt-1.5">{hint}</p>}
  </div>
);

const Input = ({ onChange, value, type, placeholder, disabled, "data-testid": testid, className, ...rest }) => (
  // Adapter: wraps PremiumInput (tone="dark") and shims the legacy
  // `onChange={(e) => setX(e.target.value)}` callsite signature so every
  // existing call in SettingsPage continues to work unmodified.
  <PremiumInput
    tone="dark"
    type={type}
    value={value ?? ""}
    onChange={(v) => onChange && onChange({ target: { value: v } })}
    placeholder={placeholder}
    disabled={disabled}
    testid={testid}
    className={className}
    {...rest}
  />
);

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
    style={{
      background: checked ? "linear-gradient(135deg, #8B5CF6, #6366F1)" : "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.10)",
    }}
  >
    <span
      className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
      style={{ transform: checked ? "translateX(18px)" : "translateX(2px)" }}
    />
  </button>
);

const ToggleRow = ({ label, description, checked, onChange, testid }) => (
  <div
    className="flex items-start gap-4 py-3"
    style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
  >
    <div className="flex-1 min-w-0">
      <p className="text-[12.5px] font-semibold text-white">{label}</p>
      {description && <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{description}</p>}
    </div>
    <Toggle checked={checked} onChange={onChange} testid={testid} />
  </div>
);

const PrefsKey = "cf:notif-prefs";
const defaultPrefs = {
  emailDigest: true,
  pushAlerts: true,
  riskAlerts: true,
  milestoneUpdates: true,
  mentionAlerts: true,
};

/* ─── Profile ──────────────────────────────────────────────────── */
const ProfileSection = ({ user }) => {
  const [name, setName] = useState(user?.full_name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(user?.full_name || ""); }, [user]);

  const save = async () => {
    if (busy || name === user?.full_name) return;
    setBusy(true);
    try {
      const res = await api.patch("/api/auth/me", { full_name: name });
      if (res && res.status >= 200 && res.status < 300) {
        toast.success("Profile updated");
      } else {
        toast.message("Profile update queued — backend endpoint coming soon.");
      }
    } catch {
      toast.message("Profile update queued — backend endpoint coming soon.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Profile" subtitle="Your personal details across the platform." testid="settings-profile">
      <div className="flex items-center gap-4 mb-6">
        <span
          className="w-14 h-14 rounded-2xl text-white text-[16px] font-bold flex items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, #8B5CF6, #4338CA)",
            boxShadow: "0 0 0 1px rgba(139,92,246,0.32)",
          }}
        >
          {initialsOf(user?.full_name || user?.email)}
        </span>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-white truncate">{user?.full_name || "—"}</p>
          <p className="text-[11.5px] text-slate-500 truncate uppercase tracking-[0.12em]">
            {user?.role || "Member"}
          </p>
          <p className="text-[10.5px] text-slate-500 mt-1.5 inline-flex items-center gap-1">
            {user?.email_verified ? (
              <><CheckCircle2 size={10} className="text-emerald-400" /> Verified</>
            ) : (
              <><AlertCircle size={10} className="text-amber-400" /> Verification pending</>
            )}
          </p>
        </div>
      </div>

      <Field label="Full name">
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="settings-name-input"
          placeholder="Your full name"
        />
      </Field>
      <Field label="Email" hint="Email changes require re-verification — contact support.">
        <Input
          type="email"
          value={user?.email || ""}
          disabled
          data-testid="settings-email-input"
        />
      </Field>
      <Field label="Role">
        <Input
          type="text"
          value={user?.role || ""}
          disabled
          data-testid="settings-role-input"
        />
      </Field>

      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={save}
          disabled={busy || name === user?.full_name}
          data-testid="settings-profile-save"
          className="h-10 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          style={{
            background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
            boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55)",
          }}
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </Card>
  );
};

/* ─── Workspace ────────────────────────────────────────────────── */
const WorkspaceSection = ({ user }) => {
  // Saved workspace name from the same source as the sidebar and switcher (/api/auth/orgs).
  const [savedName, setSavedName] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/api/auth/orgs").then(({ data }) => {
      const active = (Array.isArray(data) ? data : []).find((o) => o.org_id === user?.org_id || o.is_active);
      if (alive) setSavedName(active?.org_name || null);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.org_id]);
  const orgName = savedName || user?.org_name || "—";
  const orgId = user?.org_id || "—";
  const role = user?.role || "Member";

  return (
    <>
      <Card title="Workspace" subtitle="Organisation, branding and team-level settings." testid="settings-workspace">
        <Field label="Workspace name">
          <Input type="text" value={orgName} disabled data-testid="settings-org-name" />
        </Field>
        <Field label="Workspace ID" hint="Quote this when contacting support.">
          <Input type="text" value={orgId} disabled data-testid="settings-org-id" />
        </Field>
        <Field label="Your role">
          <Input type="text" value={role} disabled data-testid="settings-org-role" />
        </Field>
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
          Need to invite teammates? Use{" "}
          <a href="/teams" className="text-violet-300 hover:text-white underline">
            Team
          </a>{" "}
          to manage members and roles.
        </p>
      </Card>

      <RoleGate role="steward">
        <div className="mt-5">
          <SampleDataCard />
        </div>
        <div className="mt-5">
          <DemoWorkspaceCard />
        </div>
        <div className="mt-5">
          <CommunicationCard />
        </div>
        <div className="mt-5">
          <MemorandumOfSalePreambleCard />
        </div>
        <div className="mt-5">
          <OperationalSyncCard />
        </div>
      </RoleGate>
    </>
  );
};

/* ─── Memorandum of Sale preamble (director-only) ──────────────────
 * iter-398 · P0 · Optional per-org preamble that renders at the top
 * of every Memorandum of Sale (email + PDF).  Blank by default.
 * Persisted at `organizations.settings.mos_preamble`.
 */
const MemorandumOfSalePreambleCard = () => {
  const [loading, setLoading] = useState(true);
  const [preamble, setPreamble] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/v2/settings/mos-preamble");
        if (!alive) return;
        setPreamble(r.data?.preamble || "");
      } catch (_) {
        if (!alive) return;
        setError("Couldn't load MoS preamble.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/api/v2/settings/mos-preamble", { preamble });
      toast.success("Memorandum preamble saved.");
    } catch (_) {
      setError("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Memorandum of Sale preamble"
      subtitle="Optional org-wide preamble prepended to every MoS email and PDF."
      testid="settings-mos-preamble"
    >
      <p className="text-[12px] text-slate-300/80 leading-relaxed">
        Add a short opening sentence — for example, a firm-standard confidentiality clause or
        an internal reference note.  Renders as the first paragraph in both the email body and
        the PDF attachment.  Leave blank to keep the standard template.
      </p>
      <textarea
        value={preamble}
        onChange={(e) => setPreamble(e.target.value)}
        rows={4}
        maxLength={4000}
        placeholder="e.g. This Memorandum is issued subject to our standard terms of engagement. All parties should refer to their solicitors before acting on any information contained herein."
        className="mt-3 w-full rounded-lg px-3 py-2 text-[13px] text-white/90 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-violet-400/60"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="settings-mos-preamble-textarea"
      />
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10.5px] text-slate-500 tabular-nums">{preamble.length} / 4000</span>
        <button
          type="button"
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 hover:bg-violet-500 text-[12.5px] font-semibold text-white disabled:opacity-50"
          data-testid="settings-mos-preamble-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : null}
          {saving ? "Saving…" : "Save preamble"}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-[11.5px] text-rose-300/90" data-testid="settings-mos-preamble-error">{error}</p>
      )}
      {loading && (
        <p className="mt-2 text-[11.5px] text-slate-400 inline-flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Loading…
        </p>
      )}
    </Card>
  );
};


/* ─── Communication (director-only) ────────────────────────────────
 * Reply capture toggle — when ON, replies to outbound operational
 * emails land back inside the relevant Deal Room timeline instead
 * of disappearing into Outlook. Default OFF. The timeline is the
 * product — this just teaches the platform to listen.
 */
const CommunicationCard = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [domain, setDomain]   = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/workspace/communication/settings");
        if (!alive) return;
        setEnabled(!!r.data?.inbound_email_capture);
        setDomain(r.data?.inbound_domain || "");
      } catch (e) {
        if (!alive) return;
        setError("Couldn't load communication settings.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const update = async (next) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.patch("/api/workspace/communication/settings", {
        inbound_email_capture: next,
      });
      setEnabled(!!r.data?.inbound_email_capture);
      toast.success(next ? "Reply capture enabled." : "Reply capture disabled.");
    } catch (e) {
      setError("Couldn't update. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Communication"
      subtitle="Attach replies from ChainFlow emails back into transaction timelines."
      testid="settings-communication"
    >
      <p className="text-[12px] text-slate-300/80 leading-relaxed">
        When enabled, replies to chases, document requests and seller
        updates appear automatically in the relevant Deal Room — no
        Outlook forwarding, no manual pasting. The timeline stays the
        single source of truth.
      </p>

      <div className="mt-4 rounded-xl px-4 py-3.5"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-[12.5px] font-semibold text-white">Reply capture</p>
            <p className="text-[11.5px] text-slate-400 mt-1 leading-relaxed">
              Captures replies to ChainFlow-sent emails into the Deal Room timeline. Outbound identity unchanged — replies route via a private return address.
            </p>
            {domain && (
              <p className="text-[10.5px] text-slate-500 mt-2 tabular-nums">
                Return domain: <span className="text-slate-300">{domain}</span>
              </p>
            )}
          </div>
          <Toggle
            checked={enabled}
            onChange={update}
            testid="settings-comm-toggle"
          />
        </div>
      </div>

      {error && (
        <p className="mt-3 text-[11.5px] text-rose-300/90" data-testid="settings-comm-error">{error}</p>
      )}
      {loading && (
        <p className="mt-3 text-[11.5px] text-slate-400 inline-flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Loading…
        </p>
      )}
      {busy && !loading && (
        <p className="mt-3 text-[11.5px] text-slate-400 inline-flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Saving…
        </p>
      )}
    </Card>
  );
};


/* ─── Operational Sync (director-only) ─────────────────────────────
 * Calm one-tap recovery — re-aligns operational data across the
 * workspace. Surfaces the underlying Postgres mirror sync without
 * ever using infrastructure terminology in the UI. Operators heal
 * the system without leaving the app.
 */
const OperationalSyncCard = () => {
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null); // { scanned, synced, skipped, errored, duration_ms }
  const [error, setError]   = useState("");

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/api/workspace/ops/sync");
      setResult(r.data);
      toast.success("Operational data re-synced.");
    } catch (e) {
      setResult(null);
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Couldn't complete the sync. Please try again in a moment."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Operational sync"
      subtitle="Re-sync operational data across the workspace."
      testid="settings-operational-sync"
    >
      <p className="text-[12px] text-slate-300/80 leading-relaxed">
        Realigns deal milestones, activity feeds and progression signals if
        anything has drifted. Safe to run as often as you like.
      </p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="cf-press inline-flex items-center gap-2 h-9 px-4 rounded-md text-[12.5px] font-semibold bg-violet-500/15 hover:bg-violet-500/25 text-violet-100 border border-violet-400/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="settings-operational-sync-run"
          aria-busy={busy}
        >
          {busy ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              Syncing…
            </>
          ) : (
            <>
              <RefreshCw size={13} />
              Run sync
            </>
          )}
        </button>

        {result && !busy && (
          <span
            className="inline-flex items-center gap-1.5 text-[11.5px] text-emerald-300 font-medium"
            data-testid="settings-operational-sync-result"
          >
            <CheckCircle2 size={12} />
            {result.synced} of {result.scanned} re-synced
            {result.skipped > 0 && <span className="text-slate-400/70"> · {result.skipped} skipped</span>}
            {result.errored > 0 && <span className="text-amber-300/90"> · {result.errored} needs attention</span>}
            <span className="text-slate-400/60 tabular-nums">· {(result.duration_ms / 1000).toFixed(1)}s</span>
          </span>
        )}

        {error && !busy && (
          <span
            className="inline-flex items-center gap-1.5 text-[11.5px] text-amber-300/90 font-medium"
            data-testid="settings-operational-sync-error"
          >
            <AlertCircle size={12} />
            {error}
          </span>
        )}
      </div>
    </Card>
  );
};

/* ─── Sample data seeder (admin-only) ──────────────────────────────
 * Calm one-tap helper for new pilot agencies. Hits the admin-only
 * /api/workspace/seed-sample-data endpoint which writes strictly
 * into the caller's authenticated org. Idempotent on re-run.
 */
const SampleDataCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [lastRepairedAt, setLastRepairedAt] = useState(null);  // Surfaces a calm "Repaired" badge after a self-heal

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const r = await api.get("/api/workspace/seed-status");
      setStatus(r?.data || null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  // Optimistic progress copy — rotates calmly so the operator never
  // stares at a frozen label on slow mobile networks. The strings are
  // pinned to the operational language in the spec: never any
  // technical wording, never "service unavailable", never "API call".
  useEffect(() => {
    if (!seeding) {
      setProgressLabel("");
      return undefined;
    }
    const stages = [
      "Preparing workspace",
      "Adding missing contacts and properties",
      "Linking deals to participants",
      "Almost there",
    ];
    let idx = 0;
    setProgressLabel(stages[0]);
    const t = setInterval(() => {
      idx = Math.min(idx + 1, stages.length - 1);
      setProgressLabel(stages[idx]);
    }, 1800);
    return () => clearInterval(t);
  }, [seeding]);

  // Single seed call with structured-response handling. Returns
  // `{ body, err }` so the caller (onSeed) can implement
  // retry-once-on-timeout without duplicating the request shape.
  const _runSeed = async () => {
    try {
      const r = await api.post(
        "/api/workspace/seed-sample-data",
        {},
        { timeout: 60_000 },
      );
      return { body: r?.data || {}, err: null };
    } catch (err) {
      return { body: null, err };
    }
  };

  // Decide whether a failed seed call is safe to retry. We retry
  // on transport-level failures (timeouts, network errors, status 0,
  // CORS preflight failures, ERR_FAILED, ERR_NETWORK, "Failed to
  // fetch") and on the backend's own SEED_TIMEOUT response. All of
  // these indicate the pipeline didn't crash, it just ran out of
  // wall-clock or had its response stream torn. Backend writes are
  // idempotent so re-running is always safe.
  //
  // Expanded coverage (iter-153): captures axios's various network
  // failure surfaces that don't carry an HTTP response.
  const _isRetryable = (body, err) => {
    if (err) {
      if (!err.response) {
        const m = String(err.message || "").toLowerCase();
        const c = String(err.code || "").toLowerCase();
        return (
          /timeout/i.test(m)
          || /network error/i.test(m)
          || /failed to fetch/i.test(m)
          || /load failed/i.test(m)
          || /aborted/i.test(m)
          || c === "econnaborted"
          || c === "err_network"
          || c === "err_failed"
          || c === "err_internet_disconnected"
          || c === "err_connection_reset"
        );
      }
      // HTTP 5xx is also retryable — the backend pipeline is
      // idempotent so a second attempt either completes or
      // confirms the existing state.
      const s = err.response?.status;
      return typeof s === "number" && s >= 500 && s < 600;
    }
    return body?.success === false && body?.error_code === "SEED_TIMEOUT";
  };

  // Lightweight, defensive read of the workspace truth. Never throws,
  // so the success-first hard guard can rely on its return value
  // unconditionally. We capture this BEFORE the seed call (to detect
  // top-up growth) and AGAIN after the call (the canonical
  // false-negative suppression signal).
  const _readSeedStatus = async () => {
    try {
      const r = await api.get("/api/workspace/seed-status");
      return r?.data || null;
    } catch {
      return null;
    }
  };

  const onSeed = async () => {
    if (seeding) return;          // disable duplicate taps
    setSeeding(true);
    try {
      // Snapshot BEFORE — used by the "topped up" growth rule. Never
      // throws — a missing snapshot just disables the growth rule
      // without affecting the canonical populated-state success path.
      const before = await _readSeedStatus();

      let { body, err } = await _runSeed();
      // Retry-once on timeout — invisible to the operator. The
      // backend pipeline is idempotent so the second pass either
      // completes the partial write OR confirms the workspace is
      // already populated. Either outcome resolves to a calm toast.
      if (_isRetryable(body, err)) {
        const retry = await _runSeed();
        // Preserve a successful retry. Only overwrite both fields if
        // the retry produced a usable result — otherwise keep the
        // first attempt's body so the success-first hard guard can
        // still pick up a structured success body if the retry was
        // the one that fell over.
        if (retry.body && retry.body.success === true) {
          body = retry.body;
          err = null;
        } else if (retry.err && !body) {
          // First was retryable, retry also failed without a body —
          // surface the retry error so the operator sees the most
          // recent transport state.
          body = retry.body;
          err = retry.err;
        }
      }

      // Snapshot AFTER — this is the truth. Axios "Network Error"
      // can fire when the server actually persisted data but
      // Cloudflare closed the stream mid-response (iPad Safari edge
      // case). The seed pipeline is idempotent and writes the world
      // before returning, so the workspace database state — not the
      // axios result — is the authoritative outcome.
      let after = await _readSeedStatus();

      // ── Delayed final truth probe — iter-153 hardening ──────────
      // If the initial AFTER snapshot doesn't show a healthy
      // workspace AND we have an axios error in hand, the Postgres
      // write may still be in flight (autocommit lag, slow ingress,
      // a torn stream that closed before the server's commit point
      // returned). Give it ONE 1.5s grace window and re-probe before
      // resolving to rule_5 transport-error copy. This is the
      // single most common false-negative — closes the gap detected
      // in the user's live session where the seed POST appears to
      // fail but the workspace lands a moment later.
      if (err && !(after && after.is_healthy === true)) {
        await new Promise((r) => setTimeout(r, 1500));
        const re_after = await _readSeedStatus();
        if (re_after && re_after.is_healthy === true) {
          after = re_after;
        }
      }

      // Single decision surface. The evaluator handles success-first
      // logic, populated-state suppression, growth-based suppression,
      // structured backend failures and transport errors — in that
      // strict order. No code path here may render an error toast
      // when the workspace ended up populated.
      const view = evaluateSeedOutcome({ body, err, before, after });
      if (view.tone === "success") {
        toast.success(view.title, view.description ? { description: view.description } : undefined);
        if (body?.repaired) {
          setLastRepairedAt(Date.now());
        }
      } else if (view.tone === "info") {
        toast(view.title, view.description ? { description: view.description } : undefined);
      } else {
        toast.error(view.title, view.description ? { description: view.description } : undefined);
      }

      // Optimistic refresh — never make the operator hit reload. But
      // ONLY adopt the AFTER snapshot when the backend confirms the
      // workspace is healthy. A partial AFTER snapshot (e.g. deals
      // landed but contacts/properties failed) must not be cached as
      // the operational truth — that would let "Workspace populated"
      // render over a still-broken state. In every non-healthy path
      // we hard-refetch instead so the UI reflects database truth
      // and the partial banner / restore CTA can surface.
      if (after && after.is_healthy === true) {
        setStatus(after);
      } else {
        await fetchStatus();
      }

      // Fan out a workspace-seeded signal so chainStore (and every
      // other surface holding deal/property arrays) re-hydrates from
      // the canonical /api/deals + /api/properties + /api/v2/deals.
      // This is the fix for the post-seed "Could not update stage —
      // That deal could not be found in your workspace" bug — the
      // local store could otherwise hold deal IDs that the backend
      // has just replaced.
      try { window.dispatchEvent(new Event("cf:workspace-seeded")); }
      catch { /* SSR / private mode — fine */ }
    } finally {
      setSeeding(false);
    }
  };

  const total = status ? (status.contacts || 0) + (status.properties || 0) + (status.deals || 0) : 0;
  // Three-state operational truth — derived strictly from the
  // backend `seed-status` contract. Never infer "populated" from a
  // count comparison; the backend is authoritative on health.
  //
  //   isUnknown — status probe failed / hasn't completed yet.
  //   isEmpty   — every store at zero.
  //   isPartial — some stores populated, integrity not yet healthy.
  //   isHealthy — all three stores > 0 AND no orphan sample deals.
  const isUnknown = !loading && !status;
  const isEmpty   = !!status?.is_empty;
  const isHealthy = !!status?.is_healthy;
  const isPartial = !!status?.is_partial;
  // The "Repaired" badge persists for a calm window so the operator
  // notices it even if they glance away during the seed. After 12s
  // it self-clears — the next page hydrate carries no stale signal.
  const repairedRecent = lastRepairedAt && (Date.now() - lastRepairedAt) < 12_000;

  // Operational-truth headline + button copy derived from the same
  // contract. "Workspace populated" can ONLY render when isHealthy.
  const statusHeadline = loading
    ? "Checking your workspace…"
    : isUnknown
      ? "Workspace status unavailable"
      : isEmpty
        ? "Empty workspace"
        : isPartial
          ? "Sample data incomplete"
          : "Workspace populated";

  const statusToneAccent =
    loading || isUnknown ? "slate"
    : isEmpty || isPartial ? "amber"
    : "emerald";

  const ctaLabel = isEmpty
    ? "Seed sample data"
    : isPartial || isUnknown
      ? "Restore sample workspace"
      : "Top up sample data";

  return (
    <Card
      title="Sample data"
      subtitle="One-tap calm starter pack — 10 contacts · 4 properties · 4 deals — scoped strictly to your workspace. Idempotent: safe to re-run."
      testid="settings-sample-data"
    >
      {/* Visible build marker — operators verify which frontend
          bundle they are actually running before they suspect
          cached JS. Bumps with every seed-evaluator change. */}
      <div
        data-testid="seed-evaluator-build-marker"
        className="text-[10.5px] font-mono tracking-[0.04em] text-slate-500/80 mb-3"
        title={"Bundle built at " + BUNDLE_BUILT_AT}
      >
        Seed evaluator <span className="text-emerald-300/80 font-semibold">{SEED_EVALUATOR_VERSION}</span>
        <span className="text-slate-600 ml-1.5">· bundle {String(BUNDLE_BUILT_AT).slice(-12)}</span>
      </div>
      <div
        className="rounded-xl p-4 mb-4 flex items-start gap-3"
        style={{
          background:
            statusToneAccent === "emerald" ? "rgba(16,185,129,0.06)"
            : statusToneAccent === "amber"   ? "rgba(245,158,11,0.06)"
            : "rgba(99,102,241,0.06)",
          border:
            "1px solid " + (
              statusToneAccent === "emerald" ? "rgba(16,185,129,0.18)"
              : statusToneAccent === "amber"   ? "rgba(245,158,11,0.22)"
              : "rgba(99,102,241,0.18)"
            ),
        }}
        data-testid="sample-data-status"
        data-state={isEmpty ? "empty" : isPartial ? "partial" : isHealthy ? "healthy" : "unknown"}
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background:
              statusToneAccent === "emerald" ? "rgba(34,197,94,0.18)"
              : statusToneAccent === "amber"   ? "rgba(245,158,11,0.18)"
              : "rgba(148,163,184,0.18)",
          }}
        >
          {loading ? (
            <Loader2 size={15} className="animate-spin text-slate-400" />
          ) : (isEmpty || isPartial || isUnknown) ? (
            <Sprout size={15} className="text-amber-400" />
          ) : (
            <CheckCircle2 size={15} className="text-emerald-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-semibold text-white inline-flex items-center gap-2 flex-wrap">
            <span data-testid="sample-data-status-headline">{statusHeadline}</span>
            {repairedRecent && (
              <span
                className="text-[9.5px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-md inline-flex items-center gap-1"
                style={{
                  background: "rgba(16,185,129,0.16)",
                  color: "#34D399",
                  border: "1px solid rgba(16,185,129,0.28)",
                }}
                data-testid="sample-data-repaired-badge"
                title="Self-healed an incomplete sample workspace just now"
              >
                <Sprout size={9} strokeWidth={2.6} /> Repaired
              </span>
            )}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {loading ? "Reading current counts." : isUnknown ? "Tap refresh to read the latest counts from your workspace." : (
              <>
                <span className="cf-num font-semibold text-slate-200">{status?.contacts ?? 0}</span> contacts ·{" "}
                <span className="cf-num font-semibold text-slate-200">{status?.properties ?? 0}</span> properties ·{" "}
                <span className="cf-num font-semibold text-slate-200">{status?.deals ?? 0}</span> deals
                {total > 0 && " in your org."}
                {isPartial && (
                  <span className="block text-amber-300/90 mt-0.5">
                    Tap restore — ChainFlow will reconcile the missing records.
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchStatus}
          disabled={loading || seeding}
          className="text-slate-400 hover:text-white p-1 rounded-md transition-colors"
          aria-label="Refresh workspace counts"
          data-testid="sample-data-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSeed}
          disabled={seeding || loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-[12.5px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
            color: "#FFFFFF",
            boxShadow: "0 8px 22px -10px rgba(139,92,246,0.55)",
          }}
          data-testid="sample-data-seed-btn"
        >
          {seeding ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              {progressLabel || "Seeding"}…
            </>
          ) : (
            <>
              <Sprout size={13} />
              {ctaLabel}
            </>
          )}
        </button>
      </div>
      <p className="text-[10.5px] text-slate-500 mt-3 leading-relaxed">
        Tenancy-safe. Records are stamped with your <code className="text-slate-400">org_id</code> and cannot be seen by other organisations. No login accounts are created.
      </p>
    </Card>
  );
};

/* ─── Demo workspace reloader (admin/steward only) ─────────────────
 * Internal operational utility — NOT a user-facing product feature.
 * One calm tap to clear the current demo state and reload the rich
 * 30-deal operational environment (timelines, blockers, chains,
 * participants, progression activity) used for investor walkthroughs,
 * pilot resets, screenshots and onboarding realism.
 *
 * Composes two existing endpoints (no new backend surface):
 *   1. POST /api/workspace/clear-sample-data   (idempotent, demo-tagged rows only)
 *   2. POST /api/workspace/seed-rich-demo      (idempotent rich-seed)
 *
 * Real workspace data — anything without a `demo_source`/`is_demo_*`
 * tag — is never touched.
 */
const DEMO_RELOAD_STAGES = [
  "Clearing current demo data",
  "Reloading operational environment",
  "Generating timelines and activity",
  "Rebuilding live transaction workspace",
];

const DemoWorkspaceCard = () => {
  const [confirming, setConfirming] = useState(false);
  const [reloading, setReloading]   = useState(false);
  const [stageLabel, setStageLabel] = useState("");

  // Cycle calm operational progress copy while the reload pipeline runs.
  // ~2.4s between rotations so the operator never feels stranded.
  useEffect(() => {
    if (!reloading) {
      setStageLabel("");
      return undefined;
    }
    let idx = 0;
    setStageLabel(DEMO_RELOAD_STAGES[0]);
    const t = setInterval(() => {
      idx = Math.min(idx + 1, DEMO_RELOAD_STAGES.length - 1);
      setStageLabel(DEMO_RELOAD_STAGES[idx]);
    }, 2400);
    return () => clearInterval(t);
  }, [reloading]);

  const runReload = async () => {
    if (reloading) return;
    setConfirming(false);
    setReloading(true);
    try {
      // 1. Clear — never throws hard; demo-only rows; idempotent.
      try {
        await api.post("/api/workspace/clear-sample-data", {}, { timeout: 30_000 });
      } catch {
        // Clearing is best-effort. If it fails we still attempt the
        // reseed — the rich seeder is itself idempotent so a partial
        // clear simply lands as a top-up rather than a fresh build.
      }
      try { window.dispatchEvent(new Event("cf:workspace-cleared")); }
      catch { /* SSR / private mode — fine */ }

      // 2. Reload the rich operational environment.
      const r = await api.post(
        "/api/workspace/seed-rich-demo",
        {},
        { timeout: 180_000 },     // Cold rich seed can take ~35s; pad for slow networks.
      );
      const body = r?.data;
      if (body?.success) {
        const newDeals = body?.summary?.deals?.created ?? 0;
        toast.success("Demo workspace ready", {
          description: newDeals > 0
            ? `${newDeals} fresh transactions across the operational pipeline.`
            : "Operational environment refreshed.",
        });
        try { window.dispatchEvent(new Event("cf:workspace-seeded")); }
        catch { /* noop */ }
      } else {
        toast.error("Demo workspace reload couldn't complete", {
          description: "Tap again — re-running is safe.",
        });
      }
    } catch {
      toast.error("Demo workspace reload couldn't complete", {
        description: "Tap again — re-running is safe.",
      });
    } finally {
      setReloading(false);
    }
  };

  return (
    <>
      <Card
        title="Demo workspace"
        subtitle="Reload a realistic operational environment including timelines, blockers, chains, participants and progression activity."
        testid="settings-demo-workspace"
      >
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={reloading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-[12.5px] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              color: "#FFFFFF",
              boxShadow: "0 8px 22px -10px rgba(139,92,246,0.55)",
            }}
            data-testid="demo-workspace-reload-btn"
          >
            {reloading ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                {stageLabel || "Reloading"}…
              </>
            ) : (
              <>
                <RefreshCw size={13} />
                Reload demo workspace
              </>
            )}
          </button>
        </div>
        <p className="text-[10.5px] text-slate-500 mt-3 leading-relaxed">
          For demos and pilot walkthroughs. Only demo-tagged rows are touched — real workspace data is preserved.
        </p>
      </Card>

      {confirming && (
        <DemoWorkspaceConfirmDialog
          onCancel={() => setConfirming(false)}
          onConfirm={runReload}
        />
      )}
    </>
  );
};

/* ─── Calm confirmation modal — operational, never alarming ────── */
const DemoWorkspaceConfirmDialog = ({ onCancel, onConfirm }) => {
  // iter-171 polish — Safari/iOS quality:
  //  • lock body scroll so the page underneath can't rubber-band
  //  • Esc to dismiss (keyboard discipline)
  //  • `role="dialog"` + `data-state="open"` lights up the global
  //    `cfDialogIn` animation already shipped in index.css for a
  //    soft fade+lift entry (200ms cubic ease-out).
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(8,9,22,0.62)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      data-testid="demo-workspace-confirm-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={PANEL_STYLE}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-workspace-confirm-title"
        data-state="open"
        data-testid="demo-workspace-confirm-dialog"
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "rgba(139,92,246,0.16)", border: "1px solid rgba(139,92,246,0.28)" }}
          >
            <RefreshCw size={15} className="text-indigo-300" />
          </div>
          <div className="min-w-0">
            <h4 id="demo-workspace-confirm-title" className="font-display text-[15px] font-bold text-white tracking-tight">
              Reload demo workspace?
            </h4>
            <p className="text-[12px] text-slate-300/90 mt-1.5 leading-relaxed">
              This will replace the current demo data with a fresh operational environment.
            </p>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              Existing demo transactions, timelines and activity will be replaced.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-[12.5px] font-medium text-slate-300 hover:text-white hover:bg-white/[0.04] transition-colors"
            data-testid="demo-workspace-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-[12.5px] transition-all"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              color: "#FFFFFF",
              boxShadow: "0 8px 22px -10px rgba(139,92,246,0.55)",
            }}
            data-testid="demo-workspace-confirm-confirm"
          >
            Reload workspace
          </button>
        </div>
      </div>
    </div>
  );
};


/* ─── Notifications preferences ────────────────────────────────── */
const NotificationsSection = () => {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(PrefsKey);
      return { ...defaultPrefs, ...(raw ? JSON.parse(raw) : {}) };
    } catch {
      return defaultPrefs;
    }
  });

  const update = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try { localStorage.setItem(PrefsKey, JSON.stringify(next)); } catch { /* noop */ }
    // The "Email digest" toggle mirrors to the server so the daily
    // dispatcher honours it. Other toggles stay local for now.
    if (key === "emailDigest") {
      api.patch("/api/digest/preferences", { enabled: value }).catch(() => null);
    }
    toast.success("Preferences updated");
  };

  return (
    <Card title="Notifications" subtitle="Choose what we bother you about — and how." testid="settings-notifications">
      <ToggleRow
        label="Email digest"
        description="A daily operational briefing — risks, milestones and overdue actions across your transactions."
        checked={prefs.emailDigest}
        onChange={(v) => update("emailDigest", v)}
        testid="settings-toggle-email"
      />
      <DigestPreviewRow />
      <ToggleRow
        label="Push alerts"
        description="Real-time alerts for messages and document uploads."
        checked={prefs.pushAlerts}
        onChange={(v) => update("pushAlerts", v)}
        testid="settings-toggle-push"
      />
      <ToggleRow
        label="Risk alerts"
        description="Always notify me when a transaction's risk score increases."
        checked={prefs.riskAlerts}
        onChange={(v) => update("riskAlerts", v)}
        testid="settings-toggle-risk"
      />
      <ToggleRow
        label="Milestone updates"
        description="Stage progressions and key contractual milestones."
        checked={prefs.milestoneUpdates}
        onChange={(v) => update("milestoneUpdates", v)}
        testid="settings-toggle-milestones"
      />
      <ToggleRow
        label="@mentions"
        description="Notify me when someone tags me in a deal."
        checked={prefs.mentionAlerts}
        onChange={(v) => update("mentionAlerts", v)}
        testid="settings-toggle-mentions"
      />

      <BrowserPermissionRow />
      <MutedDealsRow />
    </Card>
  );
};

/* ─── Daily digest preview row ─────────────────────────────────── */
const DigestPreviewRow = () => {
  const [busy, setBusy] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/api/digest/preferences").then(({ data }) => {
      if (alive) setLastSent(data?.last_sent_at_utc || null);
    }).catch(() => null);
    return () => { alive = false; };
  }, []);

  const fmtAgo = (iso) => {
    if (!iso) return "Not sent yet";
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return "Just sent";
    if (m < 60) return `Sent ${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `Sent ${h}h ago`;
    return `Sent ${Math.floor(h / 24)}d ago`;
  };

  const sendPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/digest/send-self", {});
      if (data?.suppressed) {
        toast.success("No operational changes today — digest suppressed.");
      } else {
        toast.success(`Digest emailed to you · ${data?.items_count ?? 0} item(s).`);
        setLastSent(new Date().toISOString());
      }
    } catch {
      toast.error("Couldn't send digest preview.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-3 pt-3 mt-2"
      style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
      data-testid="settings-digest-preview-row"
    >
      <div className="min-w-0">
        <p className="text-[12.5px] font-semibold text-white">Preview today's digest</p>
        <p className="text-[11px] text-slate-400 mt-0.5">
          Sends to your inbox right now · {fmtAgo(lastSent)}
        </p>
      </div>
      <button
        type="button"
        onClick={sendPreview}
        disabled={busy}
        className="h-9 px-3.5 rounded-lg text-[11.5px] font-bold inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        style={{
          background: "linear-gradient(135deg, rgba(139,92,246,0.32) 0%, rgba(99,102,241,0.20) 100%)",
          border: "1px solid rgba(139,92,246,0.36)",
          color: "#fff",
        }}
        data-testid="settings-digest-preview-btn"
      >
        <Mail size={12} /> {busy ? "Sending…" : "Email me"}
      </button>
    </div>
  );
};

/* ─── Browser-level permission row ─────────────────────────────── */
const BrowserPermissionRow = () => {
  const [perm, setPerm] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported",
  );
  const enable = async () => {
    const result = await requestNotificationPermission();
    setPerm(result);
    if (result === "granted") {
      toast.success("Browser notifications enabled");
    } else if (result === "denied") {
      toast("Permission blocked", {
        description: "Re-enable from your browser's site settings.",
      });
    }
  };

  const meta = {
    granted:    { label: "Enabled",      tone: "emerald" },
    denied:     { label: "Blocked",      tone: "rose" },
    default:    { label: "Not enabled",  tone: "slate" },
    unsupported:{ label: "Unsupported",  tone: "slate" },
  }[perm] || { label: perm, tone: "slate" };

  const dotClass = {
    emerald: "bg-emerald-400",
    rose: "bg-rose-400",
    slate: "bg-slate-500",
  }[meta.tone];

  return (
    <div
      className="flex items-center justify-between gap-3 py-3.5 border-t border-white/[0.05]"
      data-testid="settings-browser-permission"
    >
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-white tracking-tight">Browser notifications</p>
        <p className="text-[11.5px] text-slate-400 mt-1 inline-flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
          {meta.label}
          <span className="text-slate-600">·</span>
          Calm operational nudges when a transaction needs attention.
        </p>
      </div>
      {perm !== "granted" && perm !== "unsupported" && (
        <button
          type="button"
          onClick={enable}
          className="h-9 px-3.5 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 shrink-0"
          style={{
            background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
            boxShadow: "0 10px 22px -10px rgba(124,58,237,0.7), inset 0 1px 0 rgba(255,255,255,0.18)",
          }}
          data-testid="settings-browser-permission-enable"
        >
          Enable
        </button>
      )}
    </div>
  );
};

/* ─── Muted-deals manager ──────────────────────────────────────── */
const MutedDealsRow = () => {
  const { deals } = useChainStore();
  const [muted, setMuted] = useState(getMutedDeals());

  useEffect(() => {
    const onChange = () => setMuted(getMutedDeals());
    window.addEventListener("cf:muted-changed", onChange);
    return () => window.removeEventListener("cf:muted-changed", onChange);
  }, []);

  const items = useMemo(
    () => muted.map((id) => deals.find((d) => d.id === id) || { id, name: "Untitled deal" }),
    [muted, deals],
  );

  return (
    <div className="pt-3 border-t border-white/[0.05]" data-testid="settings-muted-deals">
      <div className="flex items-center gap-2 mb-3">
        <BellOff size={13} className="text-slate-400" />
        <p className="text-[13px] font-bold text-white tracking-tight flex-1">Muted transactions</p>
        <span className="text-[10.5px] text-slate-500">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-[11.5px] text-slate-500">
          No transactions muted. Use the deal-room menu to silence a noisy deal — alerts will route to its bell only.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
              style={{
                background: "rgba(15,17,38,0.55)",
                border: "1px solid rgba(255,255,255,0.04)",
              }}
              data-testid={`settings-muted-row-${d.id}`}
            >
              <BellOff size={13} className="text-slate-500 shrink-0" />
              <span className="text-[12px] text-slate-300 truncate flex-1">{d.name || d.id}</span>
              <button
                type="button"
                onClick={() => { unmuteDeal(d.id); toast.success("Unmuted"); }}
                className="h-7 px-2.5 rounded-lg text-[11px] font-semibold text-violet-300 hover:text-white hover:bg-white/[0.06] inline-flex items-center gap-1.5 transition-colors"
                data-testid={`settings-unmute-${d.id}`}
              >
                <Volume2 size={11.5} /> Unmute
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* (formerly the closing of NotificationsSection) */
const NotificationsSectionClose = () => null;

/* ─── Operational visibility (role-sensitive) ─────────────────── */
const VisibilitySection = () => {
  const { audience, settings, setSettings } = useOperationalVisibility();

  const audienceLabel = (
    audience === AUDIENCE_INTERNAL ? "Internal — full operational updates"
      : audience === AUDIENCE_CLIENT ? "Client — softened transaction momentum"
      : "Third-party — transaction artefacts only"
  );

  return (
    <Card
      title="Operational visibility"
      subtitle="What ChainFlow surfaces about a transaction's progress. Operational — never personal presence."
      testid="settings-visibility"
    >
      <div
        className="mb-5 px-3.5 py-3 rounded-lg"
        style={{ background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.18)" }}
        data-testid="visibility-audience-summary"
      >
        <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-violet-300">Your view</p>
        <p className="text-[12.5px] text-white/85 mt-0.5">{audienceLabel}</p>
        <p className="text-[10.5px] text-slate-400 mt-1.5">
          ChainFlow shows operational momentum (documents, milestones, enquiries) —
          never online status, last seen, or read receipts.
        </p>
      </div>

      <ToggleRow
        label="Show operational updates"
        description="Surface 'what just happened' on each transaction (mortgage offer received, searches returned, draft contract uploaded)."
        checked={settings.show_operational_updates}
        onChange={(v) => setSettings({ show_operational_updates: v })}
        testid="visibility-toggle-updates"
      />
      <ToggleRow
        label="Show milestone acknowledgements"
        description="Confirmation that key contractual milestones (memorandum, exchange) have been acknowledged by the other side."
        checked={settings.show_milestone_ack}
        onChange={(v) => setSettings({ show_milestone_ack: v })}
        testid="visibility-toggle-milestones"
      />
      <ToggleRow
        label="Show document confirmations"
        description="Quiet confirmation when documents are uploaded or received on a transaction."
        checked={settings.show_document_confirmations}
        onChange={(v) => setSettings({ show_document_confirmations: v })}
        testid="visibility-toggle-documents"
      />
      <ToggleRow
        label="Hide operational timestamps"
        description="Suppress the today / yesterday / Nd ago footer under each line — keeps the surface fully calm."
        checked={settings.hide_timestamps}
        onChange={(v) => setSettings({ hide_timestamps: v })}
        testid="visibility-toggle-timestamps"
      />
      <ToggleRow
        label="Internal-only operational visibility"
        description="When a colleague views ChainFlow from outside the office, show the strictest visibility tier."
        checked={settings.internal_only}
        onChange={(v) => setSettings({ internal_only: v })}
        testid="visibility-toggle-internal-only"
      />
    </Card>
  );
};


/* ─── Integrations ─────────────────────────────────────────────── */
const integrations = [
  {
    id: "resend",
    name: "Resend",
    blurb: "Transactional emails — operational digests, invitations and risk summaries.",
    status: "Not connected",
    Icon: Mail,
  },
  {
    id: "supabase",
    name: "Supabase Storage",
    blurb: "Document uploads stored in your private bucket.",
    status: "Not connected",
    Icon: Plug,
  },
  {
    id: "chainflow-ai",
    name: "ChainFlow AI",
    blurb: "Risk forecasting, milestone summaries and operational digests powered by Claude Sonnet 4.5.",
    status: "Beta",
    Icon: Sparkles,
  },
];

const IntegrationsSection = () => (
  <Card title="Integrations" subtitle="Connect external services to extend ChainFlow." testid="settings-integrations">
    <ul className="divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
      {integrations.map((it) => (
        <li
          key={it.id}
          className="flex items-center gap-4 py-3.5 first:pt-0 last:pb-0"
          data-testid={`settings-integration-${it.id}`}
        >
          <span
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "rgba(99,102,241,0.10)",
              border: "1px solid rgba(99,102,241,0.20)",
              color: "#A5B4FC",
            }}
          >
            <it.Icon size={14} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-white truncate">{it.name}</p>
            <p className="text-[11px] text-slate-500 truncate">{it.blurb}</p>
          </div>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 h-5 rounded inline-flex items-center"
            style={
              it.status === "Beta"
                ? { background: "rgba(139,92,246,0.18)", color: "#C4B5FD", border: "1px solid rgba(139,92,246,0.32)" }
                : { background: "rgba(255,255,255,0.04)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.08)" }
            }
          >
            {it.status}
          </span>
          <button
            type="button"
            onClick={() => toast.message(`${it.name} integration is on the roadmap.`)}
            className="hidden sm:inline-flex h-9 px-3 rounded-md text-[11.5px] font-semibold text-slate-300 hover:text-white"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            Configure <ChevronRight size={11} className="ml-1" />
          </button>
        </li>
      ))}
    </ul>
  </Card>
);

/* ─── Security ─────────────────────────────────────────────────── */
const SecuritySection = ({ onSignOut }) => (
  <Card title="Security" subtitle="Sign-out and account safety." testid="settings-security">
    <ToggleRow
      label="Two-factor authentication"
      description="Add a second factor for sign-in. (Roadmap)"
      checked={false}
      onChange={() => toast.message("Two-factor authentication is on the roadmap.")}
      testid="settings-toggle-2fa"
    />
    <div className="mt-5 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <p className="text-[12.5px] font-semibold text-white">Sign out</p>
      <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
        End your current session on this browser. You'll be redirected to the sign-in screen.
      </p>
      <button
        type="button"
        onClick={onSignOut}
        data-testid="settings-sign-out"
        className="mt-3 h-9 px-3.5 rounded-lg text-[11.5px] font-bold inline-flex items-center gap-1.5"
        style={{
          background: "rgba(244,63,94,0.10)",
          border: "1px solid rgba(244,63,94,0.32)",
          color: "#FCA5A5",
        }}
      >
        <LogOut size={12} /> Sign out
      </button>
    </div>
  </Card>
);

/* ─── Page ─────────────────────────────────────────────────────── */
const VALID_TABS = new Set(["profile", "workspace", "automations", "integrations", "organisation", "notifications", "visibility", "security"]);

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t && VALID_TABS.has(t) ? t : "profile";
  })();
  const [section, setSection] = useState(initialTab);
  const [demoAvailable, setDemoAvailable] = useState(false);

  // Keep URL in sync with active tab — bookmarkable, no history pollution.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (current === section) return;
    if (section === "profile") {
      // omit ?tab= for the default — clean URL
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    } else {
      const next = new URLSearchParams(searchParams);
      next.set("tab", section);
      setSearchParams(next, { replace: true });
    }
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to back/forward changes that update ?tab=
  useEffect(() => {
    const t = searchParams.get("tab");
    const target = t && VALID_TABS.has(t) ? t : "profile";
    if (target !== section) setSection(target);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Probe the demo-control status quietly — the entry only renders
  // when the backend gate accepts this user. Failures are silent.
  useEffect(() => {
    let alive = true;
    api.get("/api/demo-control/status")
      .then(() => { if (alive) setDemoAvailable(true); })
      .catch(() => { /* gate rejected — keep entry hidden */ });
    return () => { alive = false; };
  }, []);

  const sections = useMemo(() => {
    const base = [...SECTIONS];
    if (demoAvailable) {
      base.push({ id: "demo", label: "Demo", Icon: FlaskConical });
    }
    return base;
  }, [demoAvailable]);

  const handleSignOut = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  const handleNavClick = (id) => {
    if (id === "demo") {
      nav("/settings/demo-control");
      return;
    }
    setSection(id);
  };

  const renderSection = () => {
    if (section === "profile")       return <ProfileSection user={user} />;
    if (section === "workspace")     return <WorkspaceSection user={user} />;
    if (section === "automations")   return <AutomationsPage embedded />;
    if (section === "integrations")  return <IntegrationInfrastructurePage embedded />;
    if (section === "organisation")  return <OrganisationInfrastructurePage embedded />;
    if (section === "notifications") return <NotificationsSection />;
    if (section === "visibility")    return <VisibilitySection />;
    if (section === "security")      return <SecuritySection onSignOut={handleSignOut} />;
    return null;
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: "#0A0D1A", color: "#fff" }}
      data-testid="settings-page"
    >
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen relative">
        <span aria-hidden="true" className="absolute -top-32 left-1/4 w-[800px] h-[400px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(ellipse, rgba(139,92,246,0.10), transparent 70%)" }} />

        <div className="relative px-4 sm:px-6 lg:px-10 pt-7 pb-12 max-w-5xl">
          <div className="pl-12 md:pl-0 md:pr-48 mb-7">
            <h1 className="font-display text-[28px] sm:text-[34px] font-bold tracking-[-0.025em] text-white leading-none">
              Settings
            </h1>
            <p className="text-[12.5px] text-slate-400 mt-2">
              Profile, workspace and platform configuration.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)] gap-6">
            {/* Section nav */}
            <nav
              className="self-start lg:sticky lg:top-4 rounded-2xl p-2 flex lg:flex-col gap-1 overflow-x-auto cf-scroll-dark"
              style={PANEL_STYLE}
              data-testid="settings-nav"
            >
              {sections.map(({ id, label, Icon }) => {
                const active = section === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleNavClick(id)}
                    data-testid={`settings-nav-${id}`}
                    className={`group h-10 px-3 rounded-lg flex items-center gap-2.5 text-[12.5px] font-semibold transition-colors shrink-0 ${active ? "text-white" : "text-slate-400 hover:text-white"}`}
                    style={active ? {
                      background: "linear-gradient(135deg, rgba(139,92,246,0.32) 0%, rgba(99,102,241,0.20) 100%)",
                      border: "1px solid rgba(139,92,246,0.36)",
                    } : { background: "transparent", border: "1px solid transparent" }}
                  >
                    <Icon size={13} strokeWidth={active ? 2.4 : 1.8} />
                    <span className="flex-1 text-left">{label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Section content */}
            <div className="min-w-0">{renderSection()}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
